import { CRAFTING_GROUPS, CRAFTING_GROUP_LABELS, CraftingGroup } from "game-common/item/crafting_groups"

// Narrowing the recipe list: one category at a time, plus free text over it.
//
// This replaced a row of tab chips, which had two problems the filter does not. A tab could only ever
// show ONE group, so there was no way to see the whole list at once -- and with everything now listed
// that is the view a player wants most often. And four tabs plus their counts did not fit across a
// 316px panel, so they wrapped to a second row and ate the space the list needed.
//
// Pure of pixi, like crafting_layout.ts beside it -- see __tests__/crafting_filter.test.ts.

// "All" is a real option here and deliberately FIRST: the default view is everything, and a category
// is something you reach for to cut it down.
export const ALL_GROUPS = "all" as const
export type CraftingFilterGroup = CraftingGroup | typeof ALL_GROUPS

export const FILTER_GROUPS: CraftingFilterGroup[] = [ALL_GROUPS, ...CRAFTING_GROUPS]

export const filterGroupLabel = (group: CraftingFilterGroup): string =>
    group === ALL_GROUPS ? "All" : CRAFTING_GROUP_LABELS[group]

export interface FilterableRecipe {
    name: string
    label?: string
    group?: string
    cost?: Array<{ itemType: string }>
    // Whether the Craft button on this row is live. The "can make now" toggle is exactly this field,
    // which is why the toggle is free: the server already decides it for the button.
    enabled?: boolean
}

const normalise = (value: string): string => (value || "").toLowerCase().replace(/[-_]/g, " ").trim()

/**
 * Does this recipe match what was typed?
 *
 * Matches the recipe's own name AND its ingredients, which is the half that earns the box. "rope"
 * finds the rope recipe, but it also finds the rabbit snare -- the thing you wanted the rope FOR.
 * Searching only names would make the box a faster way to reach a row you could already see, where
 * this makes it a way to ask "what can I do with these sticks".
 *
 * Hyphens are flattened to spaces on both sides so "shard metal", "shard-metal" and "metal" all land.
 */
export const matchesQuery = (recipe: FilterableRecipe, query: string): boolean => {
    const needle = normalise(query)
    if (!needle) {
        return true
    }
    if (normalise(recipe.label || recipe.name).includes(needle)) {
        return true
    }
    return (recipe.cost || []).some(ingredient => normalise(ingredient.itemType).includes(needle))
}

export const matchesGroup = (recipe: FilterableRecipe, group: CraftingFilterGroup): boolean =>
    group === ALL_GROUPS || recipe.group === group

/**
 * The rows the panel should draw, in the order the server offered them.
 *
 * Three narrowings, and they stack rather than replace each other: a category, free text over it, and
 * "only what I can make right now". The third is the fastest answer to the question a player actually
 * opens this screen with, and it is the one narrowing that gets MORE useful as the list grows -- which
 * it did, now that every recipe is listed whether or not it can be afforded.
 */
export const applyCraftingFilter = <T extends FilterableRecipe>(
    recipes: T[],
    group: CraftingFilterGroup,
    query: string,
    craftableOnly = false,
): T[] =>
    (recipes || []).filter(
        recipe =>
            matchesGroup(recipe, group) && matchesQuery(recipe, query) && (!craftableOnly || !!recipe.enabled),
    )

/**
 * What to say when a filter matches nothing.
 *
 * Names the filter that did it, because the two have different fixes and a bare "no recipes" leaves
 * the player to work out which one is in their way -- a search inside a category can hide every row
 * while the same search over everything would have found it.
 */
export const emptyFilterMessage = (
    group: CraftingFilterGroup,
    query: string,
    craftableOnly = false,
): string => {
    // "Can make now" is reported first when it is on, because it is the filter most likely to be the
    // whole reason the list is empty -- early on, almost nothing can be made yet, and a player who
    // left the toggle on would otherwise read an empty Camp list as "there is nothing to build".
    const scope = group === ALL_GROUPS ? "recipe" : `${filterGroupLabel(group)} recipe`
    if (craftableOnly && query) {
        return `No ${scope} matching "${query}" can be made yet.`
    }
    if (craftableOnly) {
        return `No ${scope} can be made yet.`
    }
    if (query) {
        return `No ${scope} matches "${query}".`
    }
    return group === ALL_GROUPS ? "No recipes available yet." : `No ${filterGroupLabel(group)} recipes yet.`
}

// How long the row a reveal landed on stays lit.
//
// The reveal already clears the filters and scrolls the row into view, but on a list of twenty-odd
// near-identical cards that still leaves the player to work out WHICH row they were just sent to.
// A second and a half is long enough to find with the eye and short enough that it is plainly a
// pointer rather than a state the row is now in.
export const REVEAL_GLOW_MS = 1500

/**
 * How bright the glow is, `elapsed` ms in. 0 before it starts and 0 once it is over.
 *
 * Rises fast, holds, then fades: the rise has to beat the eye to the row, and the fade is what stops
 * it reading as "this row is selected" once it has done its job. Pure so the shape can be checked
 * without a renderer -- and so the one thing that really matters, that it ENDS, is a test rather than
 * a thing to watch for.
 */
export const revealGlowAlpha = (elapsed: number, duration: number = REVEAL_GLOW_MS): number => {
    if (!(elapsed >= 0) || elapsed >= duration) {
        return 0
    }
    const t = elapsed / duration
    const RISE = 0.08
    const FADE_FROM = 0.55
    if (t < RISE) {
        return t / RISE
    }
    if (t < FADE_FROM) {
        return 1
    }
    return (1 - t) / (1 - FADE_FROM)
}

/**
 * Everything the panel's filter bar remembers.
 *
 * Grouped into one shape so that "what does a reveal do to this" can be answered in one place rather
 * than as a run of assignments that is easy to add a field to and easy to forget one from -- which is
 * exactly how `searchFocused` came to be left behind.
 */
export interface CraftingFilterState {
    group: CraftingFilterGroup
    query: string
    craftableOnly: boolean
    dropdownOpen: boolean
    searchFocused: boolean
}

export const INITIAL_FILTER_STATE: CraftingFilterState = {
    group: ALL_GROUPS,
    query: "",
    craftableOnly: false,
    dropdownOpen: false,
    searchFocused: false,
}

/**
 * What the filter bar looks like after "show them this recipe".
 *
 * Everything off. Any of the three narrowings can be hiding the row the reveal names, so all three
 * are cleared -- and the search box gives the keyboard back too, which is the part that was missed.
 *
 * Leaving it focused was a real bug and an instructive one: the query was cleared but the box still
 * held the keyboard and still drew itself as focused, so clicking back into it did nothing (it was
 * already focused, so the click was a no-op) while typing silently started from an empty string. The
 * box looked live, looked like it still had your text, and had neither.
 */
export const filterStateAfterReveal = (): CraftingFilterState => ({ ...INITIAL_FILTER_STATE })
